import { api } from '@/store/helpers'
import { SET_ENGAGE, SET_LOADING, SET_ERROR } from '@/store/mutation-types'

/**
 * Retry a promise with a fixed delay between attempts.
 *
 * @param {Function} fn - The function to retry
 * @param {Number} retries - Number of retries remaining
 * @param {Number} delay - Delay in milliseconds between retries
 * @return {Promise}
 */
const retryWithDelay = async (
	fn,
	getPartnerId,
	originalPartnerId,
	retries = 3,
	delay = 5000
) => {
	// Check if partner has changed before making the request
	const currentPartnerId = getPartnerId()
	if (currentPartnerId !== originalPartnerId) {
		throw new Error('Partner changed, aborting request')
	}

	try {
		return await fn()
	} catch (error) {
		if (retries > 0) {
			await new Promise(resolve => setTimeout(resolve, delay))
			return retryWithDelay(
				fn,
				getPartnerId,
				originalPartnerId,
				retries - 1,
				delay
			)
		}
		throw error
	}
}

export default {
	async getEngage({ commit, rootGetters }) {
		const partner = rootGetters['partners/active']
		if (!partner || !partner.id) {
			console.error('No active partner found')
			return
		}

		const originalPartnerId = partner.id
		const getPartnerId = () => rootGetters['partners/active']?.id

		commit(SET_LOADING, true)
		commit(SET_ERROR, false)
		try {
			const response = await retryWithDelay(
				() =>
					api()
						.partners()
						.engage(originalPartnerId),
				getPartnerId,
				originalPartnerId,
				3,
				5000
			)
			const data = response?.response
			commit(SET_ENGAGE, {
				url: data?.dashboardToken,
				expiry: data?.expiration_hours,
			})
		} catch (error) {
			// Don't show error if partner changed - a new request will be made
			if (error.message === 'Partner changed, aborting request') {
				return
			}
			console.error('Failed to fetch engage data after retries:', error)
			commit(SET_ERROR, true)
		} finally {
			// Only set loading false if partner hasn't changed
			if (getPartnerId() === originalPartnerId) {
				commit(SET_LOADING, false)
			}
		}
	},
}
